import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  User,
  Mail,
  Shield,
  FileText,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Download,
  ChevronLeft,
  Building2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { updateProfile, getUserReviews, getConsentLogs, exportUserData, deleteUserAccount } from "../lib/api";
import type { ReviewWithRelations, ConsentLog } from "../lib/types";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";

interface AccountSettingsProps {
  onNavigate?: (page: string) => void;
}

export function AccountSettings({ onNavigate }: AccountSettingsProps) {
  const { user, profile, refreshProfile } = useAuth();
  const [selectedTab, setSelectedTab] = useState("profile");
  
  // Profile form state
  const [displayName, setDisplayName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Clinic selection state
  const [clinics, setClinics] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedClinicId, setSelectedClinicId] = useState<string>("");
  const [loadingClinics, setLoadingClinics] = useState(false);
  
  // Data state
  const [reviews, setReviews] = useState<ReviewWithRelations[]>([]);
  const [consentLogs, setConsentLogs] = useState<ConsentLog[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [loadingConsent, setLoadingConsent] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load user data
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || "");
      setSelectedClinicId(profile.clinic_id || "");
    }
  }, [profile]);

  // Load clinics for patient verification
  useEffect(() => {
    const loadClinics = async () => {
      if (profile?.role !== 'patient') return;
      
      try {
        setLoadingClinics(true);
        const { data, error } = await supabase
          .from('clinics')
          .select('id, name')
          .eq('is_active', true)
          .order('name');
        
        if (error) throw error;
        setClinics(data || []);
      } catch (error) {
        console.error('Error loading clinics:', error);
        toast.error('Failed to load clinics');
      } finally {
        setLoadingClinics(false);
      }
    };

    loadClinics();
  }, [profile]);

  useEffect(() => {
    const loadReviews = async () => {
      if (!user) return;
      try {
        setLoadingReviews(true);
        const data = await getUserReviews(user.id);
        setReviews(data);
      } catch (error) {
        console.error("Error loading reviews:", error);
      } finally {
        setLoadingReviews(false);
      }
    };

    const loadConsentLogs = async () => {
      if (!user) return;
      try {
        setLoadingConsent(true);
        const data = await getConsentLogs(user.id);
        setConsentLogs(data);
      } catch (error) {
        console.error("Error loading consent logs:", error);
      } finally {
        setLoadingConsent(false);
      }
    };

    if (selectedTab === "reviews") {
      loadReviews();
    } else if (selectedTab === "privacy") {
      loadConsentLogs();
    }
  }, [selectedTab, user]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsUpdating(true);
    try {
      await updateProfile(user.id, {
        display_name: displayName,
        clinic_id: selectedClinicId || null,
      });
      await refreshProfile();
      toast.success("Profile updated successfully!");
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExportData = async () => {
    if (!user) return;

    setIsExporting(true);
    try {
      const data = await exportUserData(user.id);
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cannabias-data-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Your data has been exported!");
    } catch (error: any) {
      console.error("Error exporting data:", error);
      toast.error(error.message || "Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setIsDeleting(true);
    try {
      await deleteUserAccount(user.id);
      await supabase.auth.signOut();
      toast.success("Your account has been deleted");
      onNavigate && onNavigate("home");
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast.error(error.message || "Failed to delete account");
      setIsDeleting(false);
    }
  };

  if (!user || !profile) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-4xl mx-auto text-center py-20">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl mb-4">Authentication Required</h2>
          <p className="text-muted-foreground mb-6">
            Please sign in to access your account settings
          </p>
          <Button onClick={() => onNavigate && onNavigate("signin")}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            variant="ghost"
            className="mb-6 hover:bg-primary/10"
            onClick={() => onNavigate && onNavigate("discover")}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Discover
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-4xl mb-3">Account Settings</h1>
          <p className="text-xl text-muted-foreground">
            Manage your profile, privacy, and data
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid w-full grid-cols-4 glass-card mb-6">
              <TabsTrigger value="profile">
                <User className="w-4 h-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="reviews">
                <FileText className="w-4 h-4 mr-2" />
                Reviews
              </TabsTrigger>
              <TabsTrigger value="privacy">
                <Shield className="w-4 h-4 mr-2" />
                Privacy
              </TabsTrigger>
              <TabsTrigger value="account">
                <AlertCircle className="w-4 h-4 mr-2" />
                Account
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              {/* Organisation Settings Card (Clinic Users Only) */}
              {profile?.role === 'clinic' && (
                <Card className="p-6 glass-card mb-6 border-2 border-primary/20 bg-primary/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl gradient-green flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-xl font-semibold">Organisation Settings</h3>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        Manage your clinic's profile, logo, and subscription details
                      </p>
                      <Button 
                        onClick={() => onNavigate && onNavigate("org-settings")}
                        className="gradient-green"
                      >
                        Open Organisation Settings
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              <Card className="p-8 glass-card">
                <h2 className="text-2xl mb-6">Profile Information</h2>
                
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <div className="mt-2 p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <span>{user.email}</span>
                      <Badge variant="outline" className="ml-auto">Verified</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Email cannot be changed
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="displayName">Display Name</Label>
                    <Input
                      id="displayName"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your display name"
                      className="mt-2 h-12"
                      maxLength={50}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      This is how your name will appear on reviews
                    </p>
                  </div>

                  <div>
                    <Label>Account Role</Label>
                    <div className="mt-2 p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                      <Shield className="w-5 h-5 text-muted-foreground" />
                      <span className="capitalize">{profile.role}</span>
                    </div>
                  </div>

                  <div>
                    <Label>Member Since</Label>
                    <div className="mt-2 p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-muted-foreground" />
                      <span>{new Date(profile.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Patient Verification - Only for patients */}
                  {profile.role === 'patient' && (
                    <div>
                      <Label>Patient Verification</Label>
                      <div className="mt-2 p-4 rounded-lg border border-primary/20 bg-primary/5 space-y-4">
                        <p className="text-sm">
                          Verify your patient status to unlock enhanced features and show your verified badge on reviews.
                        </p>
                        
                        <div>
                          <Label htmlFor="clinic-select" className="text-sm mb-2 block">
                            Select Your Clinic
                          </Label>
                          <Select
                            value={selectedClinicId}
                            onValueChange={setSelectedClinicId}
                            disabled={loadingClinics}
                          >
                            <SelectTrigger id="clinic-select" className="w-full">
                              {loadingClinics ? (
                                <div className="flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Loading clinics...</span>
                                </div>
                              ) : (
                                <SelectValue placeholder="Select your clinic" />
                              )}
                            </SelectTrigger>
                            <SelectContent>
                              {clinics.map((clinic) => (
                                <SelectItem key={clinic.id} value={clinic.id}>
                                  {clinic.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-2">
                            Choose the clinic where you are registered as a patient
                          </p>
                        </div>

                        {selectedClinicId && (
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            onClick={() => onNavigate && onNavigate("patient-verification")}
                            className="gap-2"
                          >
                            <Shield className="w-4 h-4" />
                            Manage Verification
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="gradient-green hover:opacity-90 transition-opacity"
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <Card className="p-8 glass-card">
                <h2 className="text-2xl mb-6">Your Reviews</h2>
                
                {loadingReviews ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading your reviews...</p>
                  </div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-4">
                      You haven't written any reviews yet
                    </p>
                    <Button
                      onClick={() => onNavigate && onNavigate("submit-review")}
                      className="gradient-green hover:opacity-90 transition-opacity"
                    >
                      Write Your First Review
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-medium mb-1">
                              {review.product?.product_name || "Unknown Product"}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{review.stars} stars</span>
                              <span>•</span>
                              <span>{new Date(review.submitted_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Badge variant={review.mod_state === "approved" ? "default" : "secondary"}>
                            {review.mod_state}
                          </Badge>
                        </div>
                        {review.title && (
                          <p className="font-medium mb-1">{review.title}</p>
                        )}
                        {review.body && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {review.body}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Privacy Tab */}
            <TabsContent value="privacy">
              <div className="space-y-6">
                <Card className="p-8 glass-card">
                  <h2 className="text-2xl mb-6">Privacy & Data</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg mb-3">Consent History</h3>
                      {loadingConsent ? (
                        <div className="text-center py-4">
                          <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
                        </div>
                      ) : consentLogs.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No consent records found</p>
                      ) : (
                        <div className="space-y-2">
                          {consentLogs.map((log) => (
                            <div
                              key={log.id}
                              className="p-3 rounded-lg bg-muted/30 flex items-center justify-between"
                            >
                              <div>
                                <p className="text-sm font-medium">Version {log.consent_version}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(log.consented_at).toLocaleString()}
                                </p>
                              </div>
                              <CheckCircle className="w-5 h-5 text-primary" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="pt-6 border-t border-border/50">
                      <h3 className="text-lg mb-3">Export Your Data</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Download a copy of all your data including reviews, profile information, and consent history.
                      </p>
                      <Button
                        onClick={handleExportData}
                        variant="outline"
                        disabled={isExporting}
                      >
                        {isExporting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Export Data (JSON)
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Account Tab */}
            <TabsContent value="account">
              <Card className="p-8 glass-card">
                <h2 className="text-2xl mb-6">Danger Zone</h2>
                
                <div className="p-6 rounded-xl bg-destructive/10 border border-destructive/20">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg mb-2">Delete Account</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Permanently delete your account and all associated data. This action cannot be undone.
                        All your reviews will be anonymised but remain in the system.
                      </p>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Account
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. This will permanently delete your account
                              and remove your personal data from our servers. Your reviews will remain
                              but will be anonymised.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDeleteAccount}
                              className="bg-destructive hover:bg-destructive/90"
                              disabled={isDeleting}
                            >
                              {isDeleting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                "Delete Account"
                              )}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}